import { Injectable, NgZone } from '@angular/core';
import { defer, from, Observable, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import type { DocumentData, Firestore } from 'firebase/firestore';
import { LanguageService } from '@core/services/language.service';
import { LanguageEnum } from '@shared/enums/global.enum';
import { UserApi } from '@shared-features/services/user-api.service';
import { getValueByPath } from '@shared/utils/get-value-by-path.util';
import { BID_CHAT_LIMITS, CHAT_BID_STATUS, CUSTOMER_CHAT_SIDE, offerBody } from '@features/bid-chat/constants/bid-chat.constants';
import { BidChatAuthService } from '@features/bid-chat/services/bid-chat-auth.service';
import { FirestoreSdk, loadFirestoreSdk } from '@features/bid-chat/services/bid-chat-sdk';
import {
  BidChatMessage,
  BidChatRow,
  ChatConversationStatus,
  ChatMessageType,
  ChatOfferStatus,
  ChatConversationSnapshot,
  ChatCounterpartKind,
  ChatSenderType,
} from '@features/bid-chat/models/bid-chat.model';

/**
 * Converts any wire time (Firestore Timestamp, epoch ms, ISO string) to UTC ISO once, at the boundary.
 * A Timestamp is recognised by its `toDate()`, so this needs no SDK (it is loaded lazily).
 */
export const toIsoTime = (value: unknown): string | null => {
  if (value == null) return null;
  const timestamp = value as { toDate?: () => Date };
  if (typeof timestamp.toDate === 'function') return timestamp.toDate().toISOString();
  const date = new Date(value as string | number);
  return isNaN(date.getTime()) ? null : date.toISOString();
};

/** Missing or unknown status is treated as CLOSED (contract §5.1). */
const toStatus = (value: unknown): ChatConversationStatus =>
  value === ChatConversationStatus.Open ? ChatConversationStatus.Open : ChatConversationStatus.Closed;

/**
 * The bidder on a conversation doc is either a fleet or an individual driver. A driver's doc also
 * carries the fleet it sits under (e.g. "Madar Freelance Provider"), so the driver fields decide:
 * any driver field set → driver, otherwise fleet.
 */
const counterpartOf = (data: DocumentData): { counterpartKind: ChatCounterpartKind; counterpartName: string } => {
  const isDriver = data.driverId != null || !!data.driverName;
  return isDriver
    ? { counterpartKind: 'driver', counterpartName: data.individualDriverOwnerName ||  data.driverName || '' }
    : { counterpartKind: 'fleet', counterpartName: data.fleetName || '' };
};

/** Unknown or missing offer status reads as DEACTIVATED, so it never offers an action. */
const toOfferStatus = (value: unknown): ChatOfferStatus =>
  Object.values(ChatOfferStatus).includes(value as ChatOfferStatus) ? (value as ChatOfferStatus) : ChatOfferStatus.Deactivated;

/**
 * Every Firestore read and write of the bid chat (#47189). The session under them — sign-in,
 * token refresh, recovery — is `BidChatAuthService`.
 *
 * Uses the modular SDK on the app's default Firebase app, loaded on first use (`bid-chat-sdk`).
 * Snapshot callbacks are re-entered into the Angular zone so OnPush views update.
 */
@Injectable({ providedIn: 'root' })
export class BidChatFirebaseService {
  constructor(
    private zone: NgZone,
    private userApi: UserApi,
    private auth: BidChatAuthService,
    private languageService: LanguageService
  ) {}

  private db(sdk: FirestoreSdk): Firestore {
    return sdk.getFirestore(this.auth.app);
  }

  /** A write inside a valid session, once the SDK is loaded. */
  private async write<T>(call: (sdk: FirestoreSdk, db: Firestore) => Promise<T>): Promise<T> {
    const sdk = await loadFirestoreSdk();
    return this.auth.withValidSession(() => call(sdk, this.db(sdk)));
  }

  private get currentUser() {
    return this.userApi.currentUser$.value;
  }

  // -------------------------------------------------------------------------------------------
  // Reads
  // -------------------------------------------------------------------------------------------

  /**
   * Active chats, live: the customer's negotiations still running (`status == OPEN`,
   * `bidStatus in [PENDING, ACCEPTED]`, `customerId == parentId`), newest activity first, capped at 50 (contract §6.2).
   * Index: status ASC, bidStatus ASC, customerId ASC, lastMessageAt DESC.
   */
  activeInbox$(): Observable<BidChatRow[]> {
    // Read at subscribe time: the user can change between creating the stream and using it.
    return defer(() => {
      const customerId = this.currentUser?.parentId;
      if (!customerId) return of([] as BidChatRow[]);

      return this.snapshot$<BidChatRow[]>((sdk, db, next, fail) =>
        sdk.onSnapshot(
          sdk.query(
            sdk.collection(db, 'conversations'),
            sdk.where('status', '==', ChatConversationStatus.Open),
            sdk.where('bidStatus', 'in', [...CUSTOMER_CHAT_SIDE.activeBidStatuses]),
            sdk.where(CUSTOMER_CHAT_SIDE.inboxField, '==', customerId),
            sdk.orderBy('lastMessageAt', 'desc'),
            sdk.limit(BID_CHAT_LIMITS.INBOX_LIMIT)
          ),
          (snapshot) => next(snapshot.docs.map((item) => this.toRow(item.id, item.data({ serverTimestamps: 'estimate' })))),
          fail
        )
      );
    });
  }

  /**
   * One conversation, live: its status (it can close while the thread is on screen) and what the
   * header shows. A missing doc is expected 14 days after close, so it emits `null` and the caller
   * keeps the last known state.
   */
  conversation$(firestoreConversationId: string): Observable<ChatConversationSnapshot | null> {
    return this.snapshot$((sdk, db, next, fail) =>
      sdk.onSnapshot(
        sdk.doc(db, 'conversations', firestoreConversationId),
        (snapshot) => {
          if (!snapshot.exists()) return next(null);
          const data = snapshot.data();
          const counterpart = counterpartOf(data);
          // Each side has its own flag; the counterpart's kind says which one applies.
          const isVerified = counterpart.counterpartKind === 'driver' ? data.isDriverVerified : data.isFleetVerified;
          next({
            status: toStatus(data.status),
            bidStatus: data.bidStatus ?? null,
            header: {
              counterpartKind: counterpart.counterpartKind,
              // Empty stays out, so the bid's name still shows.
              ...(counterpart.counterpartName ? { counterpartName: counterpart.counterpartName } : {}),
              ...(typeof isVerified === 'boolean' ? { isVerified } : {}),
              ...(Number(data.originalBidPrice) > 0 ? { originalBid: Number(data.originalBidPrice) } : {}),
            },
          });
        },
        fail
      )
    );
  }

  /**
   * Messages oldest first, by server time (estimated for this client's writes that have not landed).
   * Metadata changes are included so a message emits again when the server confirms it.
   */
  messages$(firestoreConversationId: string): Observable<BidChatMessage[]> {
    return this.snapshot$((sdk, db, next, fail) =>
      sdk.onSnapshot(
        sdk.query(sdk.collection(db, 'conversations', firestoreConversationId, 'messages'), sdk.orderBy('createdAt')),
        { includeMetadataChanges: true },
        (snapshot) =>
          next(
            snapshot.docs.map((item) => {
              const data = item.data({ serverTimestamps: 'estimate' });
              return {
                id: item.id,
                senderType: data.senderType as ChatSenderType,
                senderUserId: data.senderUserId != null ? String(data.senderUserId) : null,
                senderName: data.senderName || '',
                type: data.type === ChatMessageType.Offer ? ChatMessageType.Offer : ChatMessageType.Text,
                body: data.body || '',
                offerPrice: typeof data.offerPrice === 'number' ? data.offerPrice : null,
                isCounterOffer: data.isCounterOffer === true,
                offerStatus: data.type === ChatMessageType.Offer ? toOfferStatus(data.offerStatus) : null,
                sentAt: toIsoTime(data.createdAt) ?? toIsoTime(data.clientCreatedAt) ?? new Date().toISOString(),
                delivery: item.metadata.hasPendingWrites ? 'sending' : 'sent',
              } as BidChatMessage;
            })
          ),
        fail
      )
    );
  }

  // -------------------------------------------------------------------------------------------
  // Writes
  // -------------------------------------------------------------------------------------------

  /**
   * A TEXT message: one atomic batch with the message doc, the conversation's preview fields and the
   * other side's unread counter (contract §6.5). `messageId` is the idempotency key; pass the same
   * one when retrying.
   */
  sendMessage(firestoreConversationId: string, body: string, messageId: string, counterpartKind: ChatCounterpartKind): Promise<void> {
    const text = (body || '').trim();
    if (!text || text.length > BID_CHAT_LIMITS.BODY_MAX_LENGTH) {
      return Promise.reject(new Error('Invalid message'));
    }
    return this.writeMessage(firestoreConversationId, messageId, { type: ChatMessageType.Text, body: text }, counterpartKind);
  }

  /**
   * An OFFER message (backend handoff "Bid negotiation"): written like TEXT, plus `offerPrice`,
   * `isCounterOffer` and `offerStatus: ACTIVE`. `body` keeps a readable line for older apps and the push.
   *
   * One transaction: the offer on the table (the conversation's `activeOfferMessageId`) is closed —
   * REJECTED if it was the fleet's, DEACTIVATED if it was mine — and the new one takes its place.
   * A transaction, not a batch: that id has to be read first, and two racing offers must not both
   * stay ACTIVE.
   */
  sendOffer(
    firestoreConversationId: string,
    offerPrice: number,
    isCounterOffer: boolean,
    messageId: string,
    counterpartKind: ChatCounterpartKind
  ): Promise<void> {
    const price = Number(offerPrice);
    if (!(price > 0)) {
      return Promise.reject(new Error('Offer price must be greater than zero'));
    }
    const senderName = this.senderName();
    if (!senderName) {
      return Promise.reject(new Error('Invalid sender'));
    }
    const content = { type: ChatMessageType.Offer, body: offerBody(price), offerPrice: price, isCounterOffer, offerStatus: ChatOfferStatus.Active };

    return this.write((sdk, db) =>
      sdk.runTransaction(db, async (transaction) => {
        const conversationRef = sdk.doc(db, 'conversations', firestoreConversationId);
        const conversation = await transaction.get(conversationRef);
        const previousId = conversation.get('activeOfferMessageId') as string | null | undefined;
        // A retry of an offer that did land: it is already the active one, write nothing twice.
        if (previousId === messageId) return;

        // Checked on the server's current doc, not the screen: the bid may have been accepted (or the
        // chat closed) since the offer row was shown, e.g. from the bids list or another tab.
        const bidStatus = conversation.get('bidStatus') as string | null | undefined;
        if (toStatus(conversation.get('status')) !== ChatConversationStatus.Open || (bidStatus && bidStatus !== CHAT_BID_STATUS.Pending)) {
          throw new Error('The negotiation is no longer open for offers');
        }

        if (previousId) {
          // Countering the fleet's offer rejects it; replacing my own just deactivates it. Only an
          // offer still ACTIVE is touched: an ACCEPTED one must never be overwritten.
          // Read before any write: Firestore transactions require all reads first.
          const previousRef = sdk.doc(conversationRef, 'messages', previousId);
          const previous = await transaction.get(previousRef);
          if (previous.exists() && previous.get('offerStatus') === ChatOfferStatus.Active) {
            const isMine = previous.get('senderType') === CUSTOMER_CHAT_SIDE.senderType;
            transaction.update(previousRef, { offerStatus: isMine ? ChatOfferStatus.Deactivated : ChatOfferStatus.Rejected });
          }
        }
        transaction.set(sdk.doc(conversationRef, 'messages', messageId), this.messageDoc(sdk, senderName, content));
        transaction.update(conversationRef, { ...this.conversationUpdate(sdk, content.body, counterpartKind), activeOfferMessageId: messageId });
      })
    );
  }

  private writeMessage(
    firestoreConversationId: string,
    messageId: string,
    content: { type: ChatMessageType; body: string },
    counterpartKind: ChatCounterpartKind
  ): Promise<void> {
    const senderName = this.senderName();
    if (!senderName) {
      return Promise.reject(new Error('Invalid sender'));
    }

    return this.write((sdk, db) => {
      const conversationRef = sdk.doc(db, 'conversations', firestoreConversationId);
      const batch = sdk.writeBatch(db);
      batch.set(sdk.doc(conversationRef, 'messages', messageId), this.messageDoc(sdk, senderName, content));
      batch.update(conversationRef, this.conversationUpdate(sdk, content.body, counterpartKind));
      return batch.commit();
    });
  }

  private messageDoc(sdk: FirestoreSdk, senderName: string, content: { type: ChatMessageType; body: string }): DocumentData {
    return {
      senderType: CUSTOMER_CHAT_SIDE.senderType,
      senderUserId: this.currentUser.id,
      senderName,
      ...content,
      createdAt: sdk.serverTimestamp(),
      clientCreatedAt: sdk.Timestamp.fromDate(new Date()),
    };
  }

  /** The conversation's preview fields and the other side's unread counter, on every send (contract §6.5). */
  private conversationUpdate(sdk: FirestoreSdk, text: string, counterpartKind: ChatCounterpartKind): DocumentData {
    return {
      [CUSTOMER_CHAT_SIDE.recipientUnreadFields[counterpartKind]]: sdk.increment(1),
      lastMessageAt: sdk.serverTimestamp(),
      lastMessageText: text.length > BID_CHAT_LIMITS.PREVIEW_LENGTH ? `${text.slice(0, BID_CHAT_LIMITS.PREVIEW_LENGTH)}…` : text,
      lastMessageSenderType: CUSTOMER_CHAT_SIDE.senderType,
    };
  }

  /** Fire-and-forget: a failure only leaves the badge stale until the next newer message (contract §7.2). */
  resetMyUnread(firestoreConversationId: string): void {
    this.write((sdk, db) => sdk.updateDoc(sdk.doc(db, 'conversations', firestoreConversationId), { [CUSTOMER_CHAT_SIDE.unreadField]: 0 })).catch(
      () => undefined
    );
  }

  // -------------------------------------------------------------------------------------------

  private senderName(): string {
    const { firstName, lastName } = this.currentUser || {};
    return `${firstName ?? ''} ${lastName ?? ''}`.trim().slice(0, BID_CHAT_LIMITS.SENDER_NAME_MAX_LENGTH);
  }

  /** Firestore reads carry no Accept-Language, so the city arrives raw (contract §9.1). */
  private cityName(city?: { nameAr?: string; nameEn?: string } | null): string | null {
    if (!city) return null;
    return this.languageService.currentLanguage$.value?.isoCode === LanguageEnum.Arabic
      ? city.nameAr ?? city.nameEn ?? null
      : city.nameEn ?? city.nameAr ?? null;
  }

  private toRow(firestoreConversationId: string, data: DocumentData): BidChatRow {
    return {
      firestoreConversationId,
      bidStatus: data.bidStatus ?? null,
      status: toStatus(data.status),
      shipmentId: data.shipmentId,
      fromCity: this.cityName(data.fromCity),
      toCity: this.cityName(data.toCity),
      ...counterpartOf(data),
      unreadCount: Number(getValueByPath<number>(data, CUSTOMER_CHAT_SIDE.unreadField)) || 0,
      lastMessageAt: toIsoTime(data.lastMessageAt),
      createdAt: toIsoTime(data.createdAt),
      lastMessagePreview: data.lastMessageText ?? null,
      lastMessageSenderType: data.lastMessageSenderType ?? null,
    };
  }

  /**
   * Loads the SDK and signs in, then streams a snapshot listener; unsubscribing detaches it. A listener
   * that fails on an expired or stale token is repaired once through the auth service and re-attached.
   */
  private snapshot$<T>(
    listen: (sdk: FirestoreSdk, db: Firestore, next: (value: T) => void, fail: (error: unknown) => void) => () => void
  ): Observable<T> {
    const attach = (isRetry: boolean): Observable<T> =>
      defer(() => from(Promise.all([loadFirestoreSdk(), this.auth.ensureSignedIn()]))).pipe(
        switchMap(
          ([sdk]) =>
            new Observable<T>((subscriber) =>
              listen(
                sdk,
                this.db(sdk),
                (value) => this.zone.run(() => subscriber.next(value)),
                (error) => this.zone.run(() => subscriber.error(error))
              )
            )
        ),
        catchError((error) =>
          isRetry ? throwError(error) : from(this.auth.recover(error)).pipe(switchMap((repaired) => (repaired ? attach(true) : throwError(error))))
        )
      );
    return attach(false);
  }
}
